import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { SearchIcon, FilterIcon, DownloadIcon, RefreshIcon } from './icons.jsx'
import { useSlashFocus } from '../lib/useSlashFocus.js'
import { scopedKey, currentScope } from '../lib/userScope.js'
import {
  MAX_COLUMN_WIDTH,
  COLUMN_WIDTH_STEP,
  COLUMN_WIDTH_STEP_LARGE,
  clampColumnWidth,
  clearColumnWidth,
  clearColumnWidths,
  hasAnyColumnWidth,
  minColumnWidth,
  pickColumnWidths,
  readWidthsFromPrefs,
  resizedWidth,
  seedColumnWidths,
  setColumnWidth,
  tablePrefsBaseKey,
  tableWidthFor,
  writeWidthsToPrefs,
} from '../lib/columnWidths.js'
import ColumnsModal from './ColumnsModal.jsx'

function ColumnsIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      {/* coolicons: line/Columns.svg */}
      <path d="M14 6.5V17.5C14 17.9647 14 18.197 14.0384 18.3902C14.1962 19.1836 14.8165 19.8036 15.6099 19.9614C15.8031 19.9999 16.0354 19.9999 16.5 19.9999C16.9647 19.9999 17.1969 19.9999 17.3901 19.9614C18.1835 19.8036 18.8036 19.1836 18.9614 18.3902C18.9999 18.197 19 17.9647 19 17.5V6.5C19 6.03535 18.9999 5.80306 18.9614 5.60986C18.8036 4.81648 18.1836 4.19624 17.3902 4.03843C17.197 4 16.9647 4 16.5 4C16.0353 4 15.8031 4 15.6099 4.03843C14.8165 4.19624 14.1962 4.81648 14.0384 5.60986C14 5.80306 14 6.03534 14 6.5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M5 6.5V17.5C5 17.9647 5 18.197 5.03843 18.3902C5.19624 19.1836 5.81648 19.8036 6.60986 19.9614C6.80306 19.9999 7.03539 19.9999 7.50004 19.9999C7.96469 19.9999 8.19694 19.9999 8.39014 19.9614C9.18352 19.8036 9.80361 19.1836 9.96143 18.3902C9.99986 18.197 10 17.9647 10 17.5V6.5C10 6.03535 9.99986 5.80306 9.96143 5.60986C9.80361 4.81648 9.18356 4.19624 8.39018 4.03843C8.19698 4 7.96466 4 7.5 4C7.03534 4 6.80306 4 6.60986 4.03843C5.81648 4.19624 5.19624 4.81648 5.03843 5.60986C5 5.80306 5 6.03534 5 6.5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

// Column visibility + rows-per-page + column widths persist per user + table.
// The table id defaults to a hash of the column keys, so every table remembers
// its own layout without callers wiring an explicit id. The id derivation lives
// in lib/columnWidths.js so the per-table isolation can be tested under node.
function prefsKeyFor(tableId, columns) {
  return scopedKey(tablePrefsBaseKey(tableId, columns.map((c) => c.key)))
}
function loadPrefs(key) {
  try {
    return JSON.parse(localStorage.getItem(key) || 'null')
  } catch {
    return null
  }
}
function savePrefs(key, prefs) {
  try {
    localStorage.setItem(key, JSON.stringify(prefs))
  } catch {
    /* ignore */
  }
}

// Sort indicator: a single caret for the active direction only — sorted
// columns show one blue ▲ or ▼, unsorted columns show nothing. `order` shows
// sort priority when several columns are combined.
function SortCaret({ dir, order }) {
  if (!dir) return null
  return (
    <span className="inline-flex items-center gap-x-[3px] text-primary">
      {/* SVG (not the ▲ glyph, whose font shape is tall): 8×8 triangle. The
          round-join stroke inflates the inset path back to full size while
          rounding all three corners. */}
      <svg
        viewBox="0 0 24 24"
        aria-hidden="true"
        className={`h-[6px] w-[6px] ${dir === 'desc' ? 'rotate-180' : ''}`}
      >
        {/* coolicons: line/Triangle.svg */}
        <path
          d="M4.37891 15.1999C3.46947 16.775 3.01489 17.5634 3.08281 18.2097C3.14206 18.7734 3.43792 19.2851 3.89648 19.6182C4.42204 20.0001 5.3309 20.0001 7.14853 20.0001H16.8515C18.6691 20.0001 19.5778 20.0001 20.1034 19.6182C20.5619 19.2851 20.8579 18.7734 20.9172 18.2097C20.9851 17.5634 20.5307 16.775 19.6212 15.1999L14.7715 6.79986C13.8621 5.22468 13.4071 4.43722 12.8135 4.17291C12.2957 3.94236 11.704 3.94236 11.1862 4.17291C10.5928 4.43711 10.1381 5.22458 9.22946 6.79845L4.37891 15.1999Z"
          fill="currentColor"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinejoin="round"
        />
      </svg>
      {order != null && (
        <span className="text-[9px] font-bold leading-none">{order}</span>
      )}
    </span>
  )
}

// Shared table for the whole app. Mirrors the reference "Tasks" table: an
// optional tab bar, a toolbar (search + filter + export + custom actions),
// icon-led sortable column headers, status pills, row hover/click, selection,
// and built-in client-side pagination.
//
// Columns: [{
//   key,                      // unique; also the default sort/search accessor
//   header,                   // header label (string or node)
//   icon,                     // optional leading header icon component
//   render(row),              // optional cell renderer (defaults to row[key])
//   sortable,                 // enable click-to-sort on this column
//   numeric / align,          // 'left' | 'right' | 'center' (numeric ⇒ right)
//   sortValue(row),           // custom sort accessor
//   className,                // extra cell/header class
//   width,                    // fixed column width (px or CSS size) — columns
//                             // without one absorb the remaining table width
//   resizable,                // set false to drop this column's drag handle
//                             // (default: resizable, see `resizableColumns`)
//   defaultHidden,            // opt-in column: listed in the organizer but not
//                             // in the fresh-table default visible set, and
//                             // never auto-surfaced into saved prefs
// }]
//
// Selection is controlled by the parent (selectable + selectedKeys +
// onToggleRow/onToggleAll) so callers can drive bulk actions; everything else
// (sort, search, tabs, pagination) is owned here unless a controlled prop is
// passed.

function StatusPill({ tone = 'neutral', icon: Icon, children }) {
  return (
    <span
      className={`inline-flex items-center gap-x-1.5 whitespace-nowrap rounded-full py-[3px] pl-2 pr-2.5 text-[12px] font-semibold ${
        {
          neutral: 'bg-panel text-dim',
          warning:
            'bg-[#fefce8] text-[#a16207] dark:bg-[rgba(161,98,7,0.18)] dark:text-[#fbbf24]',
          success:
            'bg-[#f0fdf4] text-[#15803d] dark:bg-[rgba(21,128,61,0.18)] dark:text-[#4ade80]',
          info:
            'bg-[#eff6ff] text-[#1d4ed8] dark:bg-[rgba(29,78,216,0.2)] dark:text-[#93bdfb]',
          danger:
            'bg-[#fef2f2] text-[#b91c1c] dark:bg-[rgba(185,28,28,0.2)] dark:text-[#fca5a5]',
          purple:
            'bg-[#f5f3ff] text-[#6d28d9] dark:bg-[rgba(109,40,217,0.22)] dark:text-[#c4b5fd]',
        }[tone] ?? 'bg-panel text-dim'
      }`}
    >
      {Icon && <Icon className="h-[13px] w-[13px]" />}
      {children}
    </span>
  )
}

function alignClass(col) {
  if (col.numeric || col.align === 'right') return 'text-right'
  if (col.align === 'center') return 'text-center'
  return ''
}

// Left-aligned columns keep the airy trailing gap the design is built around;
// right/center-aligned ones pad evenly so values sit against their aligned
// edge instead of floating 80px off the divider.
function padClass(col) {
  if (col.numeric || col.align === 'right' || col.align === 'center') return 'px-4'
  return 'pl-4 pr-20'
}

// The pixel total of padClass(), and it MUST stay in step with it: px-4 is
// 16 + 16, pl-4 pr-20 is 16 + 80. Because box-sizing is border-box app-wide,
// this padding is the hard lower bound on how narrow the browser will let the
// column be — a stored width below it is a number nothing on screen agrees with.
// Best-effort by design: a caller that overrides the padding through
// `col.className` gets a floor computed from the default, which is a slightly
// wrong floor rather than a wrong layout.
function padWidth(col) {
  if (col.numeric || col.align === 'right' || col.align === 'center') return 32
  return 96
}

// How narrow this particular column is allowed to get. See minColumnWidth.
function minWidthFor(col) {
  return minColumnWidth(padWidth(col))
}

// Plain name for a column, for the organizer and for the resize handle's
// screen-reader label. Unlabeled columns (an actions column with an empty
// header, or one whose header is an icon node) fall back to a title-cased key
// so neither the organizer nor the label is ever blank.
function columnLabel(col) {
  return (
    (typeof col.header === 'string' && col.header) ||
    col.pickerLabel ||
    col.key.charAt(0).toUpperCase() + col.key.slice(1)
  )
}

// The drag target on a header's right edge.
//
// INVISIBLE UNTIL POINTED AT, AND ABSOLUTELY POSITIONED, ON PURPOSE. Every
// table in the app renders this; a table nobody has resized has to look exactly
// as it did before the feature existed. Out of flow means it cannot widen a
// header cell, and opacity-0 means it cannot be seen — the header's own hover
// (and keyboard focus) is what reveals the grip.
//
// The <th> is already `position: sticky`, which is a positioned element, so it
// is the containing block here and the handle needs no `relative` of its own
// (which it could not have anyway — relative and sticky are the same property).
//
// Pointer events, not mouse events: setPointerCapture is what keeps the drag
// alive when the pointer outruns the 10px strip or leaves the window entirely,
// and it makes the handle work under touch and pen without a second code path.
// `touch-none` stops the browser claiming the gesture as a scroll first.
//
// FINE POINTERS ONLY (see `finePointer` below). Under touch there is no hover to
// reveal the grip, so it was an invisible 10px dead strip on the right edge of
// every header of every table in the app: `touch-none` plus its own click
// handler meant a tap there neither sorted the column nor scrolled the table
// sideways. A feature nobody can see must not eat the taps of people who never
// asked for it.
//
// TAB ORDER, DELIBERATELY. tabIndex={0} puts one stop per visible column in the
// way of a keyboard user reaching the first row — on the ~20-column leads table
// that is a real cost, and it was weighed against the alternative: arrow-key
// resizing is the ONLY path to this feature that does not require a mouse, and
// every roving-focus scheme needs an entry point the headers do not have (they
// are click-to-sort, not focusable). So the stops stay, and the price is paid
// back by announcing a real number: `width` is the measured width once the
// handle has focus, so the separator never reads as valueless and the first
// arrow press is not a step from nowhere.
function ResizeHandle({
  label,
  width,
  min,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  onPointerCancel,
  onDoubleClick,
  onKeyDown,
  onFocus,
  onBlur,
}) {
  return (
    <span
      role="separator"
      aria-orientation="vertical"
      aria-label={`Resize ${label} column`}
      aria-valuenow={width != null ? width : undefined}
      aria-valuemin={width != null ? min : undefined}
      aria-valuemax={width != null ? MAX_COLUMN_WIDTH : undefined}
      tabIndex={0}
      // The header's onClick sorts. A pointerdown that starts a drag, the click
      // that follows it, and a double-click that resets the width must none of
      // them reach it — otherwise every resize also re-sorts the table.
      onClick={(e) => {
        e.stopPropagation()
        e.preventDefault()
      }}
      onPointerDown={onPointerDown}
      // The move/up listeners sit on the handle itself, not on window: pointer
      // capture redirects every event for this pointer here until release, so
      // the drag survives the cursor leaving the strip without a global
      // listener that has to be torn down on unmount.
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
      onDoubleClick={onDoubleClick}
      onKeyDown={onKeyDown}
      onFocus={onFocus}
      onBlur={onBlur}
      title="Drag to resize · double-click to reset"
      className="group/resize absolute right-0 top-0 z-[1] flex h-full w-[10px] cursor-col-resize touch-none select-none items-stretch justify-end opacity-0 transition-opacity duration-150 focus:opacity-100 focus:outline-none group-hover/th:opacity-100 motion-reduce:transition-none"
    >
      {/* The visible grip. Width never transitions — an animated width would
          lag the pointer during a drag, and prefers-reduced-motion users would
          get motion the rest of the app does not give them. */}
      <span
        aria-hidden="true"
        className="my-1 w-[2px] rounded-full bg-faint group-hover/resize:bg-primary group-focus/resize:bg-primary"
      />
    </span>
  )
}

export default function DataTable({
  columns,
  rows,
  rowKey = (r) => r.id,
  defaultSort = null,
  onRowClick,
  searchable = false,
  searchKeys,
  searchValue,
  searchPlaceholder = 'Search…',
  tabs,
  activeTab,
  onTabChange,
  filterCount,
  onFilter,
  onExport,
  exportCount,
  selectable = false,
  selectedKeys,
  onToggleRow,
  onToggleAll,
  pageSize = 0,
  pageSizeOptions = [30, 50, 100, 200],
  tableId,
  columnPicker = true,
  // Drag-to-resize on every column's right edge. On by default and still
  // invisible until someone actually drags: a table with no saved widths keeps
  // the browser's auto layout, so it renders exactly as it did before this
  // existed. Pass false for a table where a resize would be meaningless (a
  // two-column summary that already fits).
  resizableColumns = true,
  // Force `table-layout: fixed` from the first render instead of waiting for a
  // resize. Only for a table that WANTS declared widths honoured exactly; the
  // default is off because switching an existing table to fixed layout changes
  // how every one of its columns is sized.
  fixedLayout = false,
  // Tighter vertical rhythm for a log-style table whose value is scanning many
  // rows at once (the audit log), not reading one. Horizontal padding, column
  // widths and padWidth() are deliberately untouched — only the vertical
  // padding changes, so a dense table still lines up with every other one.
  // Off by default: every existing table keeps the exact row height it had.
  dense = false,
  // One sorted column at a time: clicking a new header REPLACES the sort
  // instead of appending a secondary one, so the caret never carries an order
  // number. Off by default - every other table keeps multi-column sorting.
  // The leads directory turns it on (user request, 2026-10-11).
  singleSort = false,
  // Optional controlled open state for the column picker, so a parent can drive
  // it from its own "Columns" button (e.g. the leads page toolbar).
  columnsOpen,
  onColumnsOpenChange,
  emptyMessage = 'Nothing here yet.',
  // While true the table keeps its full chrome (toolbar, header, footer) and
  // the body shows a centered spinner — no layout jump when the rows land.
  loading = false,
  loadingMessage = 'Loading…',
  banner = null,
  // When true, the table fills its (height-constrained) parent and the body is
  // the only scroll region — header and footer stay put. Otherwise it caps at a
  // viewport-relative max-height.
  fill = false,
  // When true, the table is exactly as tall as its rows: no viewport cap and
  // no inner scroll, so a short table never crops its last row and the page
  // scrolls instead. For small tables like the team roster (user request,
  // 2026-10-12). Ignored when `fill` is set.
  fitToContent = false,
}) {
  // Column visibility/order + rows-per-page, persisted per user + table. The
  // signed-in account is a dependency, not just an input: a memo without it
  // would keep saving into the previous user's key after a switch.
  const scope = currentScope()
  const prefsK = useMemo(() => prefsKeyFor(tableId, columns), [tableId, columns, scope])
  const [visibleKeys, setVisibleKeys] = useState(() => {
    const all = columns.map((c) => c.key)
    const p = loadPrefs(prefsK)
    // No prefs (or an empty saved set) → every column except the opt-in ones.
    if (!p?.visibleKeys?.length) return columns.filter((c) => !c.defaultHidden).map((c) => c.key)
    const kept = p.visibleKeys.filter((k) => all.includes(k))
    if (!kept.length) return all
    // Surface columns shipped AFTER the user last saved (not ones they hid).
    // They go in at their DECLARED position, not on the end: a column added as
    // "third" has to read as third for people who already have saved prefs,
    // while everything they reordered by hand stays where they put it.
    // `knownKeys` records every column that existed when the user last saved,
    // so anything missing from it is genuinely new. Prefs written before that
    // field existed have no way to distinguish "new" from "deliberately
    // hidden" — resurrecting hidden columns there would undo the user's
    // choices, so only columns marked alwaysShow are added in that case.
    const legacy = !p.knownKeys
    const known = p.knownKeys || []
    const added = all.filter((k) => {
      if (kept.includes(k)) return false
      const col = columns.find((c) => c.key === k)
      // `alwaysShow` means exactly that, in EVERY prefs generation. A column
      // that carries the table's sort has to be on screen, and a column that
      // was previously opt-in is recorded in knownKeys — so without this the
      // modern-prefs branch below would keep hiding it and the table would sort
      // by something the user cannot see.
      if (col?.alwaysShow) return true
      // Opt-in columns never auto-surface — the user turns them on by hand.
      if (col?.defaultHidden) return false
      // Modern prefs: knownKeys is authoritative, so anything absent is new.
      if (!legacy) return !known.includes(k)
      // Legacy prefs: a missing column could be new OR deliberately hidden, so
      // only surface the ones a caller marked as must-show.
      return Boolean(col?.alwaysShow)
    })
    const merged = [...kept]
    for (const key of added) {
      const declared = all.indexOf(key)
      // Sit just after the last kept column that precedes it in the declared
      // order. Anchoring to what comes BEFORE keeps a hand-reordered table
      // sane — anchoring to what comes after would shove the new column to the
      // very front whenever the user moved a late column up.
      let at = 0
      merged.forEach((k, i) => {
        if (all.indexOf(k) < declared) at = i + 1
      })
      merged.splice(at, 0, key)
    }
    return merged
  })
  const [pageSizeState, setPageSizeState] = useState(() => loadPrefs(prefsK)?.pageSize ?? pageSize)
  // Widths the user has dragged, keyed by column. Sanitised on the way in, so a
  // hand-edited or half-written localStorage blob costs at most the layout, and
  // pruned to the columns this table still declares so a renamed column cannot
  // leave the table permanently in fixed layout with nothing to show for it.
  // Each column's own floor rides along, so a stored width that the box model
  // cannot honour (below the cell's own padding) is raised on load instead of
  // being rendered as a number that disagrees with the column on screen.
  const [colWidths, setColWidths] = useState(() =>
    pickColumnWidths(
      readWidthsFromPrefs(loadPrefs(prefsK)),
      columns.map((c) => c.key),
      Object.fromEntries(columns.map((c) => [c.key, minWidthFor(c)])),
    ),
  )
  const [pickerOpenInner, setPickerOpenInner] = useState(false)
  // Controlled when the parent passes columnsOpen; otherwise self-managed.
  const controlledOpen = columnsOpen !== undefined
  const pickerOpen = controlledOpen ? columnsOpen : pickerOpenInner
  const setPickerOpen = (v) => {
    if (onColumnsOpenChange) onColumnsOpenChange(v)
    if (!controlledOpen) setPickerOpenInner(v)
  }
  // Show the built-in toolbar button only when the parent isn't driving it.
  const showColumnsButton = columnPicker && !controlledOpen

  const displayColumns = useMemo(
    () => visibleKeys.map((k) => columns.find((c) => c.key === k)).filter(Boolean),
    [visibleKeys, columns],
  )
  const pickerColumns = useMemo(
    () =>
      columns.map((c) => ({ key: c.key, label: columnLabel(c) })),
    [columns],
  )
  function saveCols({ visibleKeys: vk, pageSize: ps }) {
    const all = columns.map((c) => c.key)
    const next = vk.length ? vk : all // never persist a zero-column (blank) table
    setVisibleKeys(next)
    setPageSizeState(ps)
    // Carry the widths through. The organizer knows nothing about them, so a
    // plain write here would silently reset every column the user had dragged
    // the moment they hid one column.
    savePrefs(prefsK, writeWidthsToPrefs({ visibleKeys: next, knownKeys: all, pageSize: ps }, colWidths))
    setSorts((prev) => prev.filter((s) => next.includes(s.key))) // drop sorts on now-hidden columns
    setPickerOpen(false)
    setPage(1)
  }

  // ── Column resizing ───────────────────────────────────────────────────────
  const tableRef = useRef(null)
  const headRefs = useRef(new Map())
  // { key, pointerId, startX, startWidth, otherWidth } for the drag in flight.
  // A ref, not state: it changes on every pointermove and nothing renders from it.
  const dragRef = useRef(null)
  // The measured width of the handle that currently has keyboard focus, so the
  // separator can announce a real aria-valuenow before the first resize. State,
  // not a ref, because it has to reach the DOM — but it only ever changes on
  // focus/blur, never during a drag, so it costs one render per tab stop.
  const [focusWidth, setFocusWidth] = useState(null)
  // Touch and pen devices never get the hover that reveals the grip, so they
  // must not get the handle at all — see the note on ResizeHandle. Read in an
  // effect rather than during render: matchMedia does not exist on the server,
  // and the first paint has to match the markup either way.
  const [finePointer, setFinePointer] = useState(false)
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined
    const mq = window.matchMedia('(hover: hover) and (pointer: fine)')
    const sync = () => setFinePointer(!!mq.matches)
    sync()
    // A laptop with a touchscreen can flip between the two (and the older
    // addListener spelling is still the only one in some embedded webviews).
    if (mq.addEventListener) {
      mq.addEventListener('change', sync)
      return () => mq.removeEventListener('change', sync)
    }
    mq.addListener?.(sync)
    return () => mq.removeListener?.(sync)
  }, [])

  // Commit widths to state AND to the prefs blob.
  //
  // The blob is re-read rather than rebuilt from this component's state so the
  // write only ever touches `colWidths`. Writing a `visibleKeys` here would be
  // a real regression: the loader treats a saved visibleKeys as "this user has
  // chosen their columns" and stops surfacing newly shipped ones into it, so
  // dragging a column wider would quietly freeze the table's column set.
  function commitWidths(next) {
    setColWidths(next)
    savePrefs(prefsK, writeWidthsToPrefs(loadPrefs(prefsK), next))
  }

  // Measure what the browser is showing right now and pin every column to it,
  // so the switch from auto to fixed layout moves nothing except the column
  // being dragged. See seedColumnWidths for why this matters.
  function seedFromDom(base) {
    return seedColumnWidths(
      base,
      displayColumns.map((c) => ({
        key: c.key,
        width: headRefs.current.get(c.key)?.getBoundingClientRect().width,
        min: minWidthFor(c),
      })),
    )
  }

  // The selection checkbox column is a fixed w-10 (2.5rem) and is not resizable,
  // but it is still part of the table's total width.
  const SELECT_COL_WIDTH = 40
  // See the `dense` prop. Header stays a little taller than the body so the
  // column labels keep their separation from the first row.
  const headPadY = dense ? 'py-2' : 'py-3'
  const cellPadY = dense ? 'py-1.5' : 'py-3'

  function beginResize(e, col) {
    // Left button / primary contact only — a right-click here should open the
    // context menu, not start a drag that can never be released.
    if (e.button !== 0) return
    e.preventDefault()
    e.stopPropagation()
    const seeded = seedFromDom(colWidths)
    const startWidth = seeded[col.key]
    // No measurement (the table is display:none in a collapsed panel) means no
    // honest start width, so there is nothing to drag from. Fail soft: leave
    // the column alone rather than resize from a made-up number.
    if (!startWidth) return
    const total = tableWidthFor(seeded, displayColumns.map((c) => c.key), selectable ? SELECT_COL_WIDTH : 0)
    dragRef.current = {
      key: col.key,
      pointerId: e.pointerId,
      startX: e.clientX,
      startWidth,
      // This column's own floor, captured once: every clamp for the rest of the
      // drag has to use it, not the global minimum, or the drag can paint a
      // width narrower than the cell's padding can ever be.
      min: minWidthFor(col),
      // Everything except the dragged column, so the running total during the
      // drag is one addition instead of a re-sum per pointermove.
      otherWidth: total == null ? null : total - startWidth,
      // What to go back to if the pointer never actually moves. Pressing on a
      // handle seeds every column, which is the right thing DURING a drag and
      // the wrong thing after a stray click or the two clicks that make up a
      // double-click reset: neither should quietly pin a table nobody resized.
      prev: colWidths,
      moved: false,
    }
    try {
      e.currentTarget.setPointerCapture(e.pointerId)
    } catch {
      /* capture unsupported — the drag still works while the pointer stays put */
    }
    setColWidths(seeded)
  }

  // DELIBERATELY IMPERATIVE. A pointermove fires per frame, and re-rendering a
  // 200-row × 8-column table on each one drops frames badly enough that the
  // handle visibly lags the cursor. Only two DOM nodes actually change during a
  // drag — the header cell and the table's own width — so the drag paints them
  // directly and the React state is updated once, on release. React never
  // fights this: it writes style properties by diffing against its OWN previous
  // style object, and the value it settles on at pointerup is the same one
  // painted here.
  function onResizeMove(e) {
    const d = dragRef.current
    if (!d || e.pointerId !== d.pointerId) return
    const dx = e.clientX - d.startX
    // A couple of pixels of jitter is a click, not a drag — fingers and
    // trackpads both wobble, and a 1px "resize" from a click on the handle is
    // indistinguishable from a bug.
    if (!d.moved && Math.abs(dx) <= 2) return
    d.moved = true
    const w = resizedWidth(d.startWidth, dx, d.min)
    if (w == null) return
    const th = headRefs.current.get(d.key)
    if (th) th.style.width = `${w}px`
    if (tableRef.current && d.otherWidth != null) tableRef.current.style.width = `${d.otherWidth + w}px`
  }

  function endResize(e) {
    const d = dragRef.current
    if (!d || (e.pointerId != null && e.pointerId !== d.pointerId)) return
    dragRef.current = null
    try {
      if (e.currentTarget.hasPointerCapture?.(d.pointerId)) e.currentTarget.releasePointerCapture(d.pointerId)
    } catch {
      /* already released */
    }
    if (!d.moved) {
      // Nothing was dragged: undo the seed and leave the stored prefs alone.
      setColWidths(d.prev)
      return
    }
    const w = resizedWidth(d.startWidth, e.clientX - d.startX, d.min)
    commitWidths(setColumnWidth(seedFromDom(colWidths), d.key, w == null ? d.startWidth : w, d.min))
  }

  // Double-click a handle: that one column goes back to the browser's layout.
  // The rest keep their widths, and the table stays in fixed layout with this
  // column as the one that absorbs the slack.
  function resetOneWidth(e, col) {
    e.preventDefault()
    e.stopPropagation()
    // A double-click arrives after two pointerdown/up pairs. Neither moved, so
    // endResize has already undone their seeds; clearing the ref is belt and
    // braces for a browser that fires dblclick without a matching pointerup.
    dragRef.current = null
    // Nothing stored for this column ⇒ nothing to undo. Without this an idle
    // double-click on any header in the app writes an empty prefs blob to
    // localStorage — the same "a write nobody asked for" the seed-undo in
    // endResize exists to prevent, and it manufactures a key for a table the
    // user never customised.
    if (colWidths[col.key] == null) return
    commitWidths(clearColumnWidth(colWidths, col.key))
  }

  // Keyboard resize. Arrow keys are the whole reason the handle is focusable —
  // a drag target with no keyboard path is a feature only some people get.
  function onResizeKey(e, col) {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return
    e.preventDefault()
    e.stopPropagation()
    const min = minWidthFor(col)
    const seeded = seedFromDom(colWidths)
    const from = seeded[col.key]
    if (!from) return
    const step = (e.shiftKey ? COLUMN_WIDTH_STEP_LARGE : COLUMN_WIDTH_STEP) * (e.key === 'ArrowLeft' ? -1 : 1)
    const next = resizedWidth(from, step, min)
    commitWidths(setColumnWidth(seeded, col.key, next, min))
    // Keep the announced value in step with the one just committed, otherwise
    // the separator keeps reading out the width it had when it took focus.
    if (next != null) setFocusWidth({ key: col.key, width: next })
  }

  // What the separator announces before anything has been resized. A column with
  // no stored width has no number to report, and a slider that announces nothing
  // gives a screen-reader user no idea what the first arrow press did — so the
  // header is measured once, when the handle takes focus.
  function onResizeFocus(col) {
    const w = clampColumnWidth(
      headRefs.current.get(col.key)?.getBoundingClientRect().width,
      minWidthFor(col),
    )
    setFocusWidth(w == null ? null : { key: col.key, width: w })
  }

  // Any stored width at all — what makes the "Reset column widths" way back
  // appear. Deliberately not limited to the VISIBLE columns: a width on a
  // column the user has since hidden is still theirs to clear.
  const anyWidths = hasAnyColumnWidth(colWidths)
  // THE SAFETY PROPERTY: no width on anything currently on screen ⇒ no fixed
  // layout, no inline table width, no behaviour change. A table nobody has
  // resized renders through exactly the same code path it did before resizing
  // existed — and so does one whose only resized column is hidden, which would
  // otherwise flip to fixed layout with no visible reason.
  const useFixedLayout = fixedLayout || displayColumns.some((c) => colWidths[c.key] != null)
  const explicitTableWidth = useFixedLayout
    ? tableWidthFor(colWidths, displayColumns.map((c) => c.key), selectable ? SELECT_COL_WIDTH : 0)
    : null

  // The width to render for a column: what the user dragged, else whatever the
  // column declared. Unchanged from before for any column with neither.
  function widthStyleFor(col) {
    const w = colWidths[col.key]
    if (w != null) return { width: `${w}px` }
    return col.width ? { width: col.width } : undefined
  }

  // Sorts are an ordered list — headers click to combine multiple columns.
  const [sorts, setSorts] = useState(() => (defaultSort ? [defaultSort] : []))
  const [query, setQuery] = useState('')
  const [page, setPage] = useState(1)
  const [uncontrolledTab, setUncontrolledTab] = useState(tabs?.[0]?.key)
  const headerCbRef = useRef(null)
  const searchInputRef = useRef(null)
  const tabsRef = useRef(null)
  const [tabPill, setTabPill] = useState({ left: 0, top: 0, width: 0, height: 0, visible: false })

  const tab = activeTab ?? uncontrolledTab
  const setTab = onTabChange ?? setUncontrolledTab

  // Slide a single highlight to the active tab (like the By state/By city toggle).
  useLayoutEffect(() => {
    const box = tabsRef.current
    if (!box) return
    const measure = () => {
      const el = box.querySelector('[data-tab-active="true"]')
      if (!el) {
        setTabPill((p) => (p.visible ? { ...p, visible: false } : p))
        return
      }
      // Measure against the rendered rects (robust to layout quirks).
      const boxRect = box.getBoundingClientRect()
      const elRect = el.getBoundingClientRect()
      setTabPill({
        left: elRect.left - boxRect.left,
        top: elRect.top - boxRect.top,
        width: elRect.width,
        height: elRect.height,
        visible: true,
      })
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(box)
    return () => ro.disconnect()
  }, [tab, tabs])

  // Reset to first page whenever the effective result set changes.
  useEffect(() => {
    setPage(1)
  }, [rows, query, tab, pageSizeState])

  const searched = useMemo(() => {
    // Tab predicate first, then free-text search.
    let list = rows
    const active = tabs?.find((t) => t.key === tab)
    if (active?.predicate) list = list.filter(active.predicate)

    const q = query.trim().toLowerCase()
    if (!q) return list
    const accessor =
      searchValue ||
      ((row) =>
        (searchKeys || columns.map((c) => c.key))
          .map((k) => row[k])
          .filter((v) => v != null)
          .join(' '))
    return list.filter((row) => String(accessor(row)).toLowerCase().includes(q))
  }, [rows, columns, query, tab, tabs, searchKeys, searchValue])

  const sorted = useMemo(() => {
    // Resolve each active sort to its column, in priority order.
    const active = sorts
      .map((s) => ({ s, col: columns.find((c) => c.key === s.key) }))
      .filter((x) => x.col)
    if (!active.length) return searched
    const copy = [...searched]
    copy.sort((a, b) => {
      for (const { s, col } of active) {
        const val = col.sortValue || ((row) => row[s.key])
        const av = val(a) ?? ''
        const bv = val(b) ?? ''
        if (av === '' && bv === '') continue // tie on this key → fall through to next
        if (av === '') return 1 // empties always sort last
        if (bv === '') return -1
        let cmp
        if (col.numeric || typeof av === 'number') cmp = Number(av) - Number(bv)
        else cmp = String(av).toLowerCase().localeCompare(String(bv).toLowerCase())
        if (cmp !== 0) return s.dir === 'asc' ? cmp : -cmp
      }
      return 0
    })
    return copy
  }, [searched, sorts, columns])

  // Click a header to cycle its column: off → asc → desc → off. New columns
  // append (lower priority), so clicking "State" then "Company" sorts by state,
  // then company within each state - unless `singleSort`, where the new column
  // replaces whatever was sorted before.
  function toggleSort(key) {
    setPage(1)
    setSorts((prev) => {
      const idx = prev.findIndex((s) => s.key === key)
      if (idx === -1) return singleSort ? [{ key, dir: 'asc' }] : [...prev, { key, dir: 'asc' }]
      if (prev[idx].dir === 'asc') {
        const next = [...prev]
        next[idx] = { key, dir: 'desc' }
        return next
      }
      return prev.filter((s) => s.key !== key)
    })
  }

  const paged = pageSizeState > 0
  const pageCount = paged ? Math.max(1, Math.ceil(sorted.length / pageSizeState)) : 1
  const current = Math.min(page, pageCount)
  const start = paged ? (current - 1) * pageSizeState : 0
  const pageRows = paged ? sorted.slice(start, start + pageSizeState) : sorted

  const pageKeys = pageRows.map(rowKey)
  const allPageSelected =
    selectable && pageRows.length > 0 && pageKeys.every((k) => selectedKeys?.has(k))
  const somePageSelected =
    selectable && pageKeys.some((k) => selectedKeys?.has(k))

  useEffect(() => {
    if (headerCbRef.current) {
      headerCbRef.current.indeterminate = somePageSelected && !allPageSelected
    }
  }, [somePageSelected, allPageSelected])

  // Sorting is driven by clicking column headers (see below), so the toolbar
  // only appears when there's search / filter / export / custom actions.
  // `anyWidths` is in here so the way back is always reachable: a table with no
  // toolbar at all still grows one the moment someone resizes a column, rather
  // than trapping them in a layout they cannot undo without clearing storage.
  const hasToolbar =
    searchable || onFilter || onExport || showColumnsButton || anyWidths
  const colSpan = displayColumns.length + (selectable ? 1 : 0)

  return (
    <div className={`flex flex-col gap-y-[14px] ${fill ? 'h-full min-h-0' : ''}`}>
      {tabs && tabs.length > 0 && (
        <div ref={tabsRef} className="relative flex flex-wrap gap-x-1 gap-y-1 border-b border-line pb-[2px]" role="tablist">
          {/* Sliding highlight for the active tab. */}
          <span
            aria-hidden="true"
            className="pointer-events-none absolute left-0 top-0 z-0 rounded-[8px] bg-surface shadow-card transition-[transform,width,height] duration-200 ease-out"
            style={{
              transform: `translate(${tabPill.left}px, ${tabPill.top}px)`,
              width: `${tabPill.width}px`,
              height: `${tabPill.height}px`,
              opacity: tabPill.visible ? 1 : 0,
            }}
          />
          {tabs.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={t.key === tab}
              data-tab-active={t.key === tab ? 'true' : undefined}
              className={`relative z-[1] inline-flex cursor-pointer items-center gap-x-[7px] rounded-[8px] px-3 py-2 text-[13px] font-semibold transition-colors ${
                t.key === tab
                  ? 'text-ink'
                  : 'bg-transparent text-dim hover:text-ink'
              }`}
              onClick={() => setTab(t.key)}
            >
              {t.icon && <t.icon className="h-[15px] w-[15px] opacity-70" />}
              {t.label}
              {t.count != null && (
                <span
                  className={`rounded-full px-2 py-px text-[11px] font-semibold ${
                    t.key === tab ? 'bg-primary-tint text-primary-dark' : 'bg-panel text-dim'
                  }`}
                >
                  {t.count}
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {hasToolbar && (
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-3">
          <div className="flex min-w-[200px] flex-1 items-center gap-x-2.5">
            {searchable && (
              <div className="relative min-w-[180px] max-w-[320px] flex-1">
                <SearchIcon className="pointer-events-none absolute left-[11px] top-1/2 h-[15px] w-[15px] -translate-y-1/2 text-faint" />
                <input
                  ref={searchInputRef}
                  type="search"
                  className="w-full rounded-[9px] border border-line bg-surface py-[9px] pl-[33px] pr-3 text-[14px] text-ink focus:border-primary focus:shadow-[0_0_0_3px_rgba(60,135,247,0.2)] focus:outline-none"
                  placeholder={searchPlaceholder}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
              </div>
            )}
            {onFilter && (
              <button
                type="button"
                className="inline-flex cursor-pointer items-center gap-x-[7px] rounded-[9px] border border-line bg-surface px-3 py-2 text-[13px] font-semibold text-ink hover:bg-panel"
                onClick={onFilter}
              >
                <FilterIcon className="h-[15px] w-[15px] text-dim" />
                Filter
                {filterCount ? (
                  <span className="rounded-full bg-primary-tint px-[7px] py-px text-[11px] text-primary-dark">
                    {filterCount}
                  </span>
                ) : null}
              </button>
            )}
          </div>
          <div className="flex items-center gap-x-2.5">
            {onExport && (
              <button
                type="button"
                className="inline-flex cursor-pointer items-center gap-x-1.5 rounded-[8px] border-none bg-transparent px-2.5 py-2 text-[13px] font-semibold text-dim hover:bg-panel hover:text-ink"
                onClick={onExport}
              >
                <DownloadIcon className="h-[15px] w-[15px]" />
                Export{exportCount != null ? ` (${exportCount})` : ''}
              </button>
            )}
            {showColumnsButton && (
              <button
                type="button"
                className="inline-flex cursor-pointer items-center gap-x-[7px] rounded-[9px] border border-line bg-surface px-3 py-2 text-[13px] font-semibold text-ink hover:bg-panel"
                onClick={() => setPickerOpen(true)}
                title="Choose which columns to show and rows per page"
              >
                <ColumnsIcon className="h-[15px] w-[15px] text-dim" />
                Columns
                <span className="rounded-full bg-panel px-[7px] py-px text-[11px] font-semibold text-dim">
                  {displayColumns.length}
                </span>
              </button>
            )}
            {/* Only once there is something to reset — an always-present button
                would be a permanent piece of chrome for a rarely-used escape
                hatch, and would change the toolbar of every table in the app. */}
            {anyWidths && (
              <button
                type="button"
                className="inline-flex cursor-pointer items-center gap-x-[7px] rounded-[9px] border border-line bg-surface px-3 py-2 text-[13px] font-semibold text-dim hover:bg-panel hover:text-ink"
                onClick={() => commitWidths(clearColumnWidths())}
                // Not "back to the browser's widths": a column that declares its
                // own width goes back to THAT, not to one the browser picked.
                // What this button actually undoes is the dragging, so that is
                // what it says.
                title="Undo every column width you have dragged on this table"
              >
                <RefreshIcon className="h-[15px] w-[15px] text-dim" />
                Reset column widths
              </button>
            )}
          </div>
        </div>
      )}

      {banner}

      {/* The scroll body is wrapped so the loading overlay can center itself on
          the VISIBLE box (the wrapper never scrolls), not on the full — often
          much wider — table. */}
      {/* The floor is what stops the body collapsing to a sliver, but it must
          stay small enough that the table yields first when the page above it
          gets taller (a KPI row wrapping to two rows of 136px cards). At 480 it
          refused to shrink and pushed the page's own footer below the fold; 320
          still shows the header plus ~5 rows. */}
      <div className={`relative ${fill ? 'flex min-h-[320px] flex-1 flex-col' : ''}`}>
      <div className={`overflow-auto border border-line bg-surface shadow-card ${fill ? 'min-h-0 flex-1' : fitToContent ? '' : 'max-h-[calc(100vh-480px)] min-h-[240px]'} ${paged && sorted.length > pageSizeState ? 'rounded-t-[12px]' : 'rounded-[12px]'}`}>
        {/* `table-layout: fixed` is what makes a dragged width stick — under the
            default auto layout the browser re-derives every column from its
            content and the width is a suggestion. It is applied ONLY once this
            table has a saved width (or a caller opted in), so an untouched
            table keeps the exact layout it has always had.

            The explicit width goes with it: under fixed layout a table wider
            than the sum of its columns hands the slack back out proportionally,
            which would quietly grow the column the user just narrowed. Stating
            the total instead sends the overflow to the wrapper's scrollbar.

            And `min-w-full` has to come OFF whenever that total is stated, which
            is the part that was wrong before: min-width:100% wins over the
            explicit width, so on any screen wider than the table the browser
            went right back to redistributing the slack — three columns dragged
            to their floor inside a 1200px card came out ~400px each, the stored
            number, the announced aria value and the 800px ceiling all describing
            a layout that was not on screen. A table narrower than its card now
            leaves a gap on the right instead. That is the honest trade: an empty
            strip is visible and undoable, a column that quietly refuses to
            narrow is neither. */}
        <table
          ref={tableRef}
          className={`border-collapse text-[13.5px]${explicitTableWidth != null ? '' : ' min-w-full'}`}
          style={
            useFixedLayout
              ? { tableLayout: 'fixed', width: explicitTableWidth != null ? `${explicitTableWidth}px` : undefined }
              : undefined
          }
        >
          <thead>
            <tr>
              {selectable && (
                // Corner cell: sticky on both axes (top for the header, left so
                // the selection column stays frozen under horizontal scroll).
                // The whole cell is a hit target; direct checkbox clicks are
                // excluded because the input's own onChange already fires.
                <th
                  className={`sticky left-0 top-0 z-[3] w-10 cursor-pointer select-none whitespace-nowrap [border-bottom:1px_solid_var(--table-line)] [border-right:1px_solid_var(--table-line)] bg-surface px-3 text-center text-[13px] font-medium text-dim ${headPadY}`}
                  onClick={(e) => {
                    if (e.target.tagName !== 'INPUT') onToggleAll?.(pageKeys)
                  }}
                >
                  <input
                    ref={headerCbRef}
                    type="checkbox"
                    className="h-[15px] w-[15px] cursor-pointer accent-primary"
                    checked={allPageSelected}
                    onChange={() => onToggleAll?.(pageKeys)}
                    aria-label="Select all on this page"
                  />
                </th>
              )}
              {displayColumns.map((col) => {
                const si = sorts.findIndex((s) => s.key === col.key)
                const dir = si !== -1 ? sorts[si].dir : null
                const canResize = resizableColumns && finePointer && col.resizable !== false
                return (
                  <th
                    key={col.key}
                    ref={(el) => {
                      // Measured on pointerdown to seed the other columns, and
                      // painted directly during a drag. Cleared on unmount so a
                      // hidden column cannot leave a stale node behind.
                      if (el) headRefs.current.set(col.key, el)
                      else headRefs.current.delete(col.key)
                    }}
                    onClick={col.sortable ? () => toggleSort(col.key) : undefined}
                    style={widthStyleFor(col)}
                    className={[
                      `group/th sticky top-0 z-[2] select-none whitespace-nowrap [border-bottom:1px_solid_var(--table-line)] bg-surface ${headPadY} text-left text-[13px] font-medium text-dim [&:not(:last-child)]:[border-right:1px_solid_var(--table-line)]`,
                      padClass(col),
                      alignClass(col),
                      // Under fixed layout a header is no longer at least as
                      // wide as its own label, so a narrowed column would print
                      // "Last touched" straight across the divider into the next
                      // header. Clip it to an ellipsis instead. The resize
                      // handle is inside the cell's box, so hiding the overflow
                      // does not hide the grip.
                      useFixedLayout ? 'overflow-hidden text-ellipsis' : '',
                      col.sortable ? 'group cursor-pointer hover:text-ink' : '',
                      col.className || '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    aria-sort={dir ? (dir === 'asc' ? 'ascending' : 'descending') : undefined}
                  >
                    <span
                      className={`inline-flex items-center gap-x-[7px] ${
                        col.numeric || col.align === 'right' ? 'flex-row-reverse' : ''
                      }`}
                    >
                      <span>{col.header}</span>
                      {col.sortable && (
                        <SortCaret dir={dir} order={dir && sorts.length > 1 ? si + 1 : null} />
                      )}
                    </span>
                    {canResize && (
                      <ResizeHandle
                        label={columnLabel(col)}
                        width={
                          colWidths[col.key] ??
                          (focusWidth?.key === col.key ? focusWidth.width : null)
                        }
                        min={minWidthFor(col)}
                        onFocus={() => onResizeFocus(col)}
                        onBlur={() => setFocusWidth(null)}
                        onPointerDown={(e) => beginResize(e, col)}
                        onPointerMove={onResizeMove}
                        onPointerUp={endResize}
                        onPointerCancel={endResize}
                        onDoubleClick={(e) => resetOneWidth(e, col)}
                        onKeyDown={(e) => onResizeKey(e, col)}
                      />
                    )}
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {loading ? null : pageRows.length === 0 ? (
              <tr>
                <td colSpan={colSpan} className="p-0">
                  <div className="p-10 text-center text-dim">{emptyMessage}</div>
                </td>
              </tr>
            ) : (
              pageRows.map((row) => {
                const k = rowKey(row)
                const isSelected = selectable && selectedKeys?.has(k)
                return (
                  <tr
                    key={k}
                    className={[
                      'group transition-colors duration-150',
                      onRowClick ? 'cursor-pointer' : '',
                      isSelected
                        ? 'bg-primary-tint dark:bg-[rgba(60,135,247,0.16)]'
                        : 'hover:bg-panel',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                  >
                    {selectable && (
                      // Frozen under horizontal scroll, so it needs its own
                      // opaque background mirroring the row state (the dark
                      // selected color is --primary-tint pre-blended over
                      // --surface — the translucent token would let scrolled
                      // cells ghost through).
                      <td
                        className={`sticky left-0 z-[1] w-10 cursor-pointer [border-bottom:1px_solid_var(--table-line)] [border-right:1px_solid_var(--table-line)] ${cellPadY} px-3 text-center align-middle text-ink transition-colors duration-150 ${
                          isSelected
                            ? 'bg-primary-tint dark:bg-[#283446]'
                            : 'bg-surface group-hover:bg-panel'
                        }`}
                        // Whole cell toggles; direct checkbox clicks are excluded
                        // because the input's own onChange already fires.
                        onClick={(e) => {
                          e.stopPropagation()
                          if (e.target.tagName !== 'INPUT') onToggleRow?.(k)
                        }}
                      >
                        <input
                          type="checkbox"
                          className="h-[15px] w-[15px] cursor-pointer accent-primary"
                          checked={!!isSelected}
                          onChange={() => onToggleRow?.(k)}
                          aria-label="Select row"
                        />
                      </td>
                    )}
                    {displayColumns.map((col) => {
                      const userW = colWidths[col.key]
                      // THE CAP THE USER IS ACTUALLY FIGHTING. `col.maxWidth` is
                      // what truncates the text — the <th> can be dragged to
                      // 600px and the Subject line goes on being cut at the
                      // declared 340px, leaving 260px of blank cell next to an
                      // ellipsis. So once the user has set a width, the column
                      // itself is the cap and the body may fill it. '100%'
                      // rather than `${userW}px` because the cell's padding is
                      // inside the column width: the body's share is whatever
                      // the padding box leaves, and stating the full column
                      // width here would put the last 96px back under the
                      // padding.
                      const bodyMaxWidth = userW != null ? '100%' : col.maxWidth
                      // Only a plain string can honestly be offered as the
                      // tooltip for clipped content; the text inside a rendered
                      // node is not ours to guess at.
                      const cellText = typeof row[col.key] === 'string' ? row[col.key] : undefined
                      return (
                        <td
                          key={col.key}
                          className={[
                            `[border-bottom:1px_solid_var(--table-line)] ${cellPadY} align-middle text-ink [&:not(:last-child)]:[border-right:1px_solid_var(--table-line)]`,
                            padClass(col),
                            // Pure-text cells stay on one line; columns that render
                            // wrapping badge/pill groups opt out with `wrap`.
                            col.wrap ? '' : 'whitespace-nowrap',
                            alignClass(col),
                            // Under the auto layout the table had before, a
                            // nowrap cell could never be narrower than its
                            // content. Under fixed layout it can, and nothing
                            // clipped it: narrowing `company` or `state` printed
                            // its text straight over the divider and into the
                            // neighbouring cell. Every cell gets clipped now,
                            // not just the ones that declared a maxWidth, and
                            // the full value stays reachable through the title.
                            useFixedLayout ? (col.wrap ? 'overflow-hidden' : 'overflow-hidden text-ellipsis') : '',
                            col.className || '',
                          ]
                            .filter(Boolean)
                            .join(' ')}
                          title={useFixedLayout && !bodyMaxWidth ? cellText : undefined}
                        >
                          {bodyMaxWidth ? (
                            <div
                              className="overflow-hidden text-ellipsis"
                              style={{ maxWidth: bodyMaxWidth }}
                              title={cellText}
                            >
                              {col.render ? col.render(row) : row[col.key]}
                            </div>
                          ) : col.render ? (
                            col.render(row)
                          ) : (
                            row[col.key]
                          )}
                        </td>
                      )
                    })}
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>
      {loading && (
        <div className="pointer-events-none absolute inset-0 z-[2] flex flex-col items-center justify-center gap-y-3">
          <span
            className="h-7 w-7 animate-spin rounded-full [border:2px_solid_var(--table-line)] [border-top-color:var(--primary)]"
            role="status"
            aria-label={loadingMessage}
          />
          <span className="text-[13px] text-dim">{loadingMessage}</span>
        </div>
      )}
      </div>

      {(loading || (paged && sorted.length > pageSizeState)) && (
        <div className="-mt-[14px] flex flex-wrap items-center justify-between gap-x-2.5 gap-y-2.5 rounded-b-[12px] border border-t-0 border-line bg-surface px-4 py-2.5 shadow-card">
          <span className="text-[13px] text-dim">
            {loading ? (
              'Loading…'
            ) : (
              <>
                {start + 1}–{Math.min(start + pageSizeState, sorted.length)} of{' '}
                {sorted.length.toLocaleString()}
              </>
            )}
            {selectable && selectedKeys?.size > 0 && (
              <span className="ml-2 font-semibold text-primary-dark">
                · {selectedKeys.size.toLocaleString()} selected
              </span>
            )}
          </span>
          <div className="flex items-center gap-x-2.5">
            <button
              type="button"
              className="inline-flex items-center gap-x-1.5 rounded-[8px] border border-line bg-transparent px-[14px] py-[7px] text-[13px] font-semibold text-dim transition-colors hover:bg-panel hover:text-ink disabled:cursor-not-allowed disabled:opacity-[0.45]"
              onClick={() => setPage(current - 1)}
              disabled={current <= 1}
            >
              ← Prev
            </button>
            <span className="text-[13px] text-dim">
              {loading ? 'Page 1' : `Page ${current} of ${pageCount}`}
            </span>
            <button
              type="button"
              className="inline-flex items-center gap-x-1.5 rounded-[8px] border border-line bg-transparent px-[14px] py-[7px] text-[13px] font-semibold text-dim transition-colors hover:bg-panel hover:text-ink disabled:cursor-not-allowed disabled:opacity-[0.45]"
              onClick={() => setPage(current + 1)}
              disabled={current >= pageCount}
            >
              Next →
            </button>
          </div>
        </div>
      )}

      {pickerOpen && (
        <ColumnsModal
          allColumns={pickerColumns}
          visibleKeys={visibleKeys}
          pageSize={pageSizeState}
          pageSizeOptions={pageSizeOptions}
          onSave={saveCols}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </div>
  )
}

DataTable.StatusPill = StatusPill
export { StatusPill }
